import { useMemo, useState } from "react";
import HttpMethods from "system/types/enums/HttpMethods";
import useHttpRequest from "./use-http-request";
import authentication from "system/libraries/authentication";
import useConfiguration from "./use-configuration";
import {
  AudienceCategory,
  IAlert,
  IAlertApiPayload,
  IAlertFormData,
  IAlertResult,
  IFetchAlertsResponse,
  SeverityType,
  SourceType,
} from "system/types/interfaces/IAlertFormData ";
import { ApiResponse } from "system/types/wireTypes";

const useManageAlertApi = () => {
  const [auth] = useState(authentication);
  const { currentTenant, isGlobalAdmin } = useConfiguration();

  const apiUrl = useMemo<string>(() => (process.env.REACT_APP_MANAGE_ALERT_URL ?? "") + "/alertapi/v1/", []);

  const httpRequest = useHttpRequest(apiUrl, auth);

  const getAlerts = async (): Promise<IFetchAlertsResponse> => {
    const endpoint = isGlobalAdmin ? "AlertsByGlobalAdmin" : "Alerts";
    return await httpRequest(HttpMethods.get, endpoint);
  };

  const fetchAlerts = async (): Promise<IAlertResult> => {
    const response = await getAlerts();
    const alertList = response?.data ?? [];

    const data: IAlertFormData[] = alertList.map((item: IAlert) => ({
      id: item.alertId,
      alertTitle: item.subject,
      description: item.body,
      severity: item.severityType,
      audience: [item.audienceCategory],
      status: item.sourceType,
      startDate: item.applicabilityStartTimeStamp,
      endDate: item.applicabilityEndTimeStamp,
      createdDate: item.createdTimeStamp,
      source: item.sourceType,
      recipients: item.recipients ?? [],
    }));

    return {
      data,
      unreadCount: response?.unreadCount,
    };
  };

  return useMemo(
    () => ({
      getAlerts,
      fetchAlerts,
      getAlertSeverityTypes: async (): Promise<ApiResponse<SeverityType[]>> => {
        return await httpRequest(HttpMethods.get, "AlertSeverityTypes");
      },

      getAlertAudienceCategory: async (): Promise<ApiResponse<AudienceCategory[]>> => {
        return await httpRequest(HttpMethods.get, "AlertAudienceCategory");
      },
      getAlertSourceTypes: async (): Promise<ApiResponse<SourceType[]>> => {
        return await httpRequest(HttpMethods.get, "AlertSourceTypes");
      },
      createAlert: async (data: IAlertApiPayload): Promise<ApiResponse<any>> => {
        return await httpRequest(HttpMethods.post, "PostAlert", data);
      },

      updateAlert: async (data: IAlertApiPayload): Promise<ApiResponse<any>> => {
        return await httpRequest(HttpMethods.post, "UpdateAlert", data);
      },

      deleteAlert: async (alertId: number): Promise<boolean> => {
        return await httpRequest(HttpMethods.get, `DeleteAlert?alertId=${alertId}`);
      },
      isReadAlert: async (recipientId: number, isRead: boolean): Promise<boolean> => {
        return await httpRequest(HttpMethods.post, `IsReadAlert/${recipientId}/${isRead}`);
      },
      disableAlert: async (alertId: number): Promise<boolean> => {
        return await httpRequest(HttpMethods.post, `DisableAlert/${alertId}`);
      },
      markAllReadAlerts: async (): Promise<boolean> => {
        return await httpRequest(HttpMethods.post, "MarkAllReadAlerts");
      },
    }),
    [auth, httpRequest, currentTenant, isGlobalAdmin]
  );
};

export default useManageAlertApi;
